import { z } from "zod";
import { VARIABLE_PAY_CADENCES } from "@/features/payroll/schemas/variable-pay.schema";

/**
 * Wire-shape mirror of the shore's variable pay BATCH DTOs (PAYD-FR-015 to
 * FR-018, `variable-pay-payout.dto.ts`, `variable-pay-accrual.dto.ts` and
 * `variable-pay-me.dto.ts`).
 *
 * Two things this file is deliberately literal about.
 *
 * The pay actions are the payroll RUN's four, re-exported by the shore's
 * vocabulary rather than restated: `pay`, `hold_payout`, `void`,
 * `paid_outside`. There is no `partial`. A part-paid line is a `pay` line
 * whose `finalAmount` was edited down with a reason, which is the same record
 * with one fewer concept in it.
 *
 * `materialisedAt` is null on an approved batch until the additional salary
 * landing rule (PAYD-FR-128, workstream C-14) ships. The batch records the
 * approval; it does not yet create the pay rows, and the screen has to say so.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const CURRENCY = /^[A-Z]{3}$/;
const DECIMAL = /^-?\d{1,15}(\.\d{1,6})?$/;

export const VARIABLE_PAY_LINE_ACTIONS = ["pay", "hold_payout", "void", "paid_outside"] as const;
export type VariablePayLineAction = (typeof VARIABLE_PAY_LINE_ACTIONS)[number];

/// The only action that owes an additional salary row when the batch lands.
export const MATERIALISING_LINE_ACTION: VariablePayLineAction = "pay";

/// FR-015 reason chips. Each answers "why is this line not just the plan's
/// headline number", which is the only question a reviewer of 200 rows has
/// time to ask.
export const VARIABLE_PAY_CHIPS = [
  "prorated",
  "no_rating",
  "no_basis_line",
  "awaiting_settlement",
  "multiplier",
  "carried_forward",
] as const;
export type VariablePayChip = (typeof VARIABLE_PAY_CHIPS)[number];

export const VARIABLE_PAY_PAYOUT_STATUSES = ["draft", "approved", "cancelled"] as const;
export type VariablePayPayoutStatus = (typeof VARIABLE_PAY_PAYOUT_STATUSES)[number];

export const variablePayPayoutLineSchema = z
  .object({
    id: z.string(),
    membershipId: z.string(),
    /// Who this line pays, named by the shore. Required, never optional: the
    /// browser cannot resolve it. Every roster this screen could join against
    /// is live members only, and a batch covers a whole period, so a line for
    /// somebody who resigned inside it used to render "Member" on the row and
    /// "A member" in the bulk-pay failure toast.
    memberName: z.string(),
    assignmentId: z.string().nullable(),
    basisAmount: z.string(),
    prorationFactor: z.string(),
    multiplier: z.string(),
    ratingValue: z.string().nullable(),
    computedAmount: z.string(),
    finalAmount: z.string(),
    editReason: z.string().nullable(),
    payAction: z.enum(VARIABLE_PAY_LINE_ACTIONS),
    payActionReason: z.string().nullable(),
    chips: z.array(z.enum(VARIABLE_PAY_CHIPS)),
    additionalSalaryId: z.string().nullable(),
    carriedToPayoutId: z.string().nullable(),
    version: z.number().int(),
  })
  .strict();
export type VariablePayPayoutLine = z.infer<typeof variablePayPayoutLineSchema>;

export const variablePayPayoutLineListSchema = z
  .object({
    rows: z.array(variablePayPayoutLineSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type VariablePayPayoutLineList = z.infer<typeof variablePayPayoutLineListSchema>;

/// The count and money per pay action, which is what the approve dialog shows
/// before anybody commits.
export const variablePayPayoutTotalSchema = z
  .object({
    payAction: z.enum(VARIABLE_PAY_LINE_ACTIONS),
    count: z.number().int(),
    total: z.string(),
  })
  .strict();
export type VariablePayPayoutTotal = z.infer<typeof variablePayPayoutTotalSchema>;

export const variablePayPayoutSchema = z
  .object({
    id: z.string(),
    planId: z.string(),
    periodStart: z.string().regex(ISO_DATE),
    periodEnd: z.string().regex(ISO_DATE),
    payoutDate: z.string().regex(ISO_DATE),
    status: z.enum(VARIABLE_PAY_PAYOUT_STATUSES),
    currency: z.string().regex(CURRENCY),
    ratingCycleId: z.string().nullable(),
    generatedBy: z.string().nullable(),
    generatedAt: z.string(),
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    materialisedAt: z.string().nullable(),
    materialisedCycleStart: z.string().regex(ISO_DATE).nullable(),
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    version: z.number().int(),
    totals: z.array(variablePayPayoutTotalSchema),
    created: z.boolean().optional(),
  })
  .strict();
export type VariablePayPayout = z.infer<typeof variablePayPayoutSchema>;

export const variablePayPayoutListSchema = z
  .object({
    rows: z.array(variablePayPayoutSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type VariablePayPayoutList = z.infer<typeof variablePayPayoutListSchema>;

// -- Requests -----------------------------------------------------------------

export const generateVariablePayPayoutSchema = z.object({
  /// Absent means the newest ENDED period, which is what "generate early"
  /// means: the period is over, its payout month has not arrived yet.
  periodStart: z.string().regex(ISO_DATE).optional(),
  ratingCycleId: z.string().min(1).max(40).optional(),
});
export type GenerateVariablePayPayoutInput = z.infer<typeof generateVariablePayPayoutSchema>;

/**
 * One line edit: the amount, the pay action, or both.
 *
 * The shore enforces the reasons conditionally (a reason is mandatory only
 * when the amount moves or the action is not `pay`), so the client mirrors the
 * same rule in the dialog rather than demanding a reason for a no-op save.
 */
export const updateVariablePayPayoutLineSchema = z.object({
  version: z.number().int(),
  finalAmount: z.string().regex(DECIMAL).optional(),
  editReason: z.string().min(3).max(500).optional(),
  payAction: z.enum(VARIABLE_PAY_LINE_ACTIONS).optional(),
  payActionReason: z.string().min(3).max(500).optional(),
});
export type UpdateVariablePayPayoutLineInput = z.infer<typeof updateVariablePayPayoutLineSchema>;

export const cancelWithReasonSchema = z.object({
  version: z.number().int(),
  reason: z.string().min(3).max(500),
});
export type CancelWithReasonInput = z.infer<typeof cancelWithReasonSchema>;

export const approveVariablePayPayoutSchema = z.object({
  version: z.number().int(),
  /// TX4. In the body rather than a header, matching `payment-files.dto.ts`:
  /// one convention, so a retry looks the same wherever a client is retrying.
  idempotencyKey: z.string().min(8).max(120).optional(),
});
export type ApproveVariablePayPayoutInput = z.infer<typeof approveVariablePayPayoutSchema>;

export const materialiseVariablePayPayoutSchema = z.object({
  version: z.number().int(),
  targetCycleStart: z.string().regex(ISO_DATE),
  holdInvalid: z.boolean(),
  idempotencyKey: z.string().min(8).max(120),
});
export type MaterialiseVariablePayPayoutInput = z.infer<typeof materialiseVariablePayPayoutSchema>;

export const bulkActionVariablePayPayoutLinesSchema = z.object({
  payAction: z.enum(VARIABLE_PAY_LINE_ACTIONS),
  lineIds: z.array(z.string().min(1).max(40)).optional(),
  filterPayAction: z.enum(VARIABLE_PAY_LINE_ACTIONS).optional(),
  chip: z.enum(VARIABLE_PAY_CHIPS).optional(),
  reason: z.string().min(3).max(500).optional(),
});
export type BulkActionVariablePayPayoutLinesInput = z.infer<
  typeof bulkActionVariablePayPayoutLinesSchema
>;

export const variablePayMaterialisePreviewSchema = z
  .object({
    sendable: z.boolean(),
    refusalCode: z.string().nullable(),
    targetCycleStart: z.string().regex(ISO_DATE),
    targetCycleLabel: z.string().nullable(),
    targetCycleState: z.string().nullable(),
    coveringRunId: z.string().nullable(),
    coveringRunStatus: z.string().nullable(),
    payableCount: z.number().int(),
    payableTotal: z.string(),
    holdCount: z.number().int(),
    voidCount: z.number().int(),
    paidOutsideCount: z.number().int(),
    invalidCount: z.number().int(),
    invalidLines: z.array(
      z.object({
        id: z.string(),
        memberName: z.string(),
        reasons: z.array(z.string()),
      }),
    ),
    invalidOmitted: z.number().int(),
    suggestedCycleStart: z.string().regex(ISO_DATE).nullable(),
  })
  .strict();
export type VariablePayMaterialisePreview = z.infer<typeof variablePayMaterialisePreviewSchema>;

export const bulkActionResultSchema = z.object({ updated: z.number().int() }).strict();
export type BulkActionResult = z.infer<typeof bulkActionResultSchema>;

// -- Accrual (FR-017) ---------------------------------------------------------

export const variablePayAccrualRowSchema = z
  .object({
    membershipId: z.string(),
    assignmentId: z.string().nullable(),
    periodAmount: z.string(),
    prorationFactor: z.string(),
    multiplier: z.string(),
    accruedAmount: z.string(),
    payAction: z.enum(VARIABLE_PAY_LINE_ACTIONS),
    chips: z.array(z.enum(VARIABLE_PAY_CHIPS)),
  })
  .strict();
export type VariablePayAccrualRow = z.infer<typeof variablePayAccrualRowSchema>;

export const variablePayAccrualSchema = z
  .object({
    planId: z.string(),
    currency: z.string().regex(CURRENCY),
    periodStart: z.string().regex(ISO_DATE),
    periodEnd: z.string().regex(ISO_DATE),
    payoutDate: z.string().regex(ISO_DATE),
    asOf: z.string().regex(ISO_DATE),
    elapsedFraction: z.string(),
    memberCount: z.number().int(),
    total: z.string(),
    rows: z.array(variablePayAccrualRowSchema),
    nextCursor: z.string().nullable(),
  })
  .strict();
export type VariablePayAccrual = z.infer<typeof variablePayAccrualSchema>;

// -- The member's own card (FR-018) -------------------------------------------

export const variablePayMePayoutSchema = z
  .object({
    payoutId: z.string(),
    lineId: z.string(),
    periodStart: z.string().regex(ISO_DATE),
    periodEnd: z.string().regex(ISO_DATE),
    payoutDate: z.string().regex(ISO_DATE),
    amount: z.string(),
    approvedAt: z.string().nullable(),
  })
  .strict();
export type VariablePayMePayout = z.infer<typeof variablePayMePayoutSchema>;

export const variablePayMePlanSchema = z
  .object({
    planId: z.string(),
    planName: z.string(),
    cadence: z.enum(VARIABLE_PAY_CADENCES),
    currency: z.string().regex(CURRENCY),
    /// Null until a batch has been approved, which is exactly what FR-018
    /// asks for: a member sees the target after approval, not before.
    annualTarget: z.string().nullable(),
    paidToDateThisTaxYear: z.string(),
    /// "YYYY-MM", or null when the plan is archived or past its last period.
    nextPayoutMonth: z.string().nullable(),
    payouts: z.array(variablePayMePayoutSchema),
  })
  .strict();
export type VariablePayMePlan = z.infer<typeof variablePayMePlanSchema>;

export const variablePayMeListSchema = z
  .object({ rows: z.array(variablePayMePlanSchema) })
  .strict();
export type VariablePayMeList = z.infer<typeof variablePayMeListSchema>;
